import asyncRetry from "async-retry";
import { ReploError } from "schemas/reploError";

class CloudflareUploadError extends ReploError {}
class UploadCanceledError extends ReploError {}

interface SignedUrlResponse {
  signedUrl: string;
  publicUrl: string;
}

interface CloudflareUploadParams {
  file: Blob;
  filename: string;
  projectId: string;
  fileId?: string;
  fetchSignedUrl: (params: {
    projectId: string;
    filename: string;
    fileId?: string;
    contentType: string;
    contentLength: number;
  }) => Promise<SignedUrlResponse>;
  errorContext?: string;
  // Caller-owned signal: short-circuits before the signed-URL fetch and
  // aborts the in-flight XHR. Existing `setCancelHandler` continues to work
  // for callers that don't have a signal at hand.
  signal?: AbortSignal;
}

const isServerError = (status: number) => status >= 500;

export const uploadToCloudflare = async ({
  file,
  filename: originalFilename,
  projectId,
  fileId,
  fetchSignedUrl,
  errorContext = "file",
  onProgress,
  setCancelHandler,
  signal,
}: CloudflareUploadParams & {
  onProgress?: (loadedBytes: number, totalBytes: number) => void;
  setCancelHandler?: (cancel: () => void) => void;
}): Promise<string> => {
  const buildCanceledError = () => {
    return new UploadCanceledError({
      message: `Upload of ${errorContext} ${originalFilename} was canceled`,
      additionalData: { filename: originalFilename, projectId, errorContext },
    });
  };

  if (signal?.aborted) {
    throw buildCanceledError();
  }

  const { signedUrl, publicUrl } = await fetchSignedUrl({
    projectId,
    filename: originalFilename,
    fileId,
    contentType: file.type,
    contentLength: file.size,
  });

  if (signal?.aborted) {
    throw buildCanceledError();
  }

  await asyncRetry(
    async (bail) => {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedUrl, true);
        xhr.setRequestHeader("Content-Type", file.type);
        setCancelHandler?.(() => xhr.abort());
        const onAbortSignal = () => xhr.abort();
        signal?.addEventListener("abort", onAbortSignal, { once: true });
        if (onProgress) {
          xhr.upload.addEventListener("progress", (event: ProgressEvent) => {
            if (event.lengthComputable) {
              onProgress(event.loaded, event.total);
            } else {
              onProgress(event.loaded, file.size);
            }
          });
        }
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            const error = new CloudflareUploadError({
              message: `Failed to upload ${errorContext} ${originalFilename} to Cloudflare`,
              additionalData: { status: xhr.status },
            });

            // NOTE (Noah, 2026-02-16, REPL-21481): Only retry on 5xx errors
            // from Cloudflare R2. 4xx errors (auth, bad request) won't
            // resolve on retry.
            if (!isServerError(xhr.status)) {
              bail(error);
              return;
            }

            reject(error);
          }
        });
        xhr.addEventListener("abort", () => {
          signal?.removeEventListener("abort", onAbortSignal);
          bail(buildCanceledError());
        });
        xhr.addEventListener("error", () => {
          signal?.removeEventListener("abort", onAbortSignal);
          reject(
            new CloudflareUploadError({
              message: `Network error uploading ${errorContext} ${originalFilename}`,
            }),
          );
        });
        xhr.addEventListener("loadend", () => {
          signal?.removeEventListener("abort", onAbortSignal);
        });
        xhr.send(file);
      });
    },
    { retries: 3, minTimeout: 500 },
  );

  return publicUrl;
};
